import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid, Card, CardContent, Paper } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import StorageIcon from "@mui/icons-material/Storage";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MDButton from "components/MDButton";
import AppLoading from "verticales/AppLoading.tsx";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Componente de tarjeta métrica
const MetricCard = ({ title, value, icon, color }) => (
  <Card
    sx={{
      height: "100%",
      background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
    }}
  >
    <CardContent>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: "12px",
            padding: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  icon: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const ViewEC2Resizes = ({ data = [], loading = false }) => {
  const [loadingState, setLoadingState] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [dashboardMetrics, setDashboardMetrics] = useState({});

  // Función para agrupar los datos por instance_id
  const groupDataByInstanceId = (data) => {
    const groupedData = data.reduce((acc, item) => {
      const { instance_id } = item;
      if (!acc[instance_id]) {
        acc[instance_id] = {
          ...item,
          details: [{ dias: item.dias, hora_base: item.hora_base, hora_resize: item.hora_resize }],
        };
      } else {
        acc[instance_id].details.push({
          dias: item.dias,
          hora_base: item.hora_base,
          hora_resize: item.hora_resize,
        });
      }
      return acc;
    }, {});
    return Object.values(groupedData);
  };

  // Calcular métricas del dashboard
  const calculateMetrics = (data) => {
    const totalInstances = new Set(data.map((item) => item.instance_id)).size;
    const totalCuentas = new Set(data.map((item) => item.cuenta)).size;
    const totalProductos = new Set(data.map((item) => item.producto)).size;
    const totalResizes = data.length;

    // Resizes por cuenta
    const resizesPorCuenta = data.reduce((acc, item) => {
      acc[item.cuenta] = (acc[item.cuenta] || 0) + 1;
      return acc;
    }, {});

    // Resizes por producto
    const resizesPorProducto = data.reduce((acc, item) => {
      acc[item.producto] = (acc[item.producto] || 0) + 1;
      return acc;
    }, {});

    // Resizes por día de la semana
    const resizesPorDia = data.reduce((acc, item) => {
      let dias = item.dias;
      if (typeof dias === "string") {
        dias = dias.replace(/[{}]/g, "").split(",").map(Number);
      }
      if (Array.isArray(dias)) {
        dias.forEach((dia) => {
          const dayMap = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
          const dayName = dayMap[dia] || "Desconocido";
          acc[dayName] = (acc[dayName] || 0) + 1;
        });
      }
      return acc;
    }, {});

    // Convertir a arrays para gráficos
    const cuentasChartData = Object.entries(resizesPorCuenta)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const productosChartData = Object.entries(resizesPorProducto)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const diasChartData = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((dia) => ({
      dia,
      resizes: resizesPorDia[dia] || 0,
    }));

    return {
      totalInstances,
      totalCuentas,
      totalProductos,
      totalResizes,
      cuentasChartData,
      productosChartData,
      diasChartData,
    };
  };

  // Cargar datos iniciales
  const loadData = async () => {
    setLoadingState(true);
    try {
      const grouped = groupDataByInstanceId(data);
      setTableData(grouped);
      const metrics = calculateMetrics(data);
      setDashboardMetrics(metrics);
    } catch (error) {
      console.error("Error al procesar datos:", error);
    } finally {
      setLoadingState(false);
    }
  };

  // Mapear días a nombres
  const mapDays = (days) => {
    const dayMap = {
      0: "Domingo",
      1: "Lunes",
      2: "Martes",
      3: "Miércoles",
      4: "Jueves",
      5: "Viernes",
      6: "Sábado",
    };
    if (typeof days === "string") {
      days = days.replace(/[{}]/g, "").split(",").map(Number);
    }
    if (!Array.isArray(days) || days.length === 0) {
      return "No disponible";
    }
    return days.map((day) => dayMap[day] || "Día desconocido").join(", ");
  };

  // Definir columnas
  const columns = useMemo(
    () => [
      { accessorKey: "cuenta", header: "Cuenta", size: 150 },
      { accessorKey: "instance_id", header: "Instance ID", size: 200 },
      { accessorKey: "instance_name", header: "Instance Name", size: 200, enableGrouping: false },
      { accessorKey: "producto", header: "Producto", size: 200 },
    ],
    []
  );

  // Exportar datos
  const handleExportData = () => {
    console.log("Exportar datos");
  };

  // Efecto para cargar datos iniciales
  useEffect(() => {
    if (data.length > 0) {
      loadData();
    } else {
      console.warn("No se recibieron datos iniciales");
      setLoadingState(true);
    }
  }, [data]);

  // Colores para gráficos
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82ca9d"];

  return (
    <Box sx={{ marginTop: "1px" }}>
      {loadingState ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <AppLoading titleCustom={"EC2 Resizes Dashboard"} />
        </Box>
      ) : (
        <>
          {/* Dashboard de Métricas */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              EC2 Resizes Dashboard
            </Typography>

            {/* Tarjetas de Métricas */}
            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Instancias"
                  value={dashboardMetrics.totalInstances}
                  icon={<StorageIcon sx={{ color: "white", fontSize: 28 }} />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Resizes"
                  value={dashboardMetrics.totalResizes}
                  icon={<TrendingUpIcon sx={{ color: "white", fontSize: 28 }} />}
                  color="#2e7d32"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Cuentas"
                  value={dashboardMetrics.totalCuentas}
                  icon={<AccountTreeIcon sx={{ color: "white", fontSize: 28 }} />}
                  color="#ed6c02"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Productos"
                  value={dashboardMetrics.totalProductos}
                  icon={<AccessTimeIcon sx={{ color: "white", fontSize: 28 }} />}
                  color="#9c27b0"
                />
              </Grid>
            </Grid>

            {/* Gráficos */}
            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              {/* Gráfico de Barras - Resizes por Día */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ padding: 2, height: "350px" }}>
                  <Typography variant="h6" gutterBottom>
                    Resizes por Día de la Semana
                  </Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={dashboardMetrics.diasChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dia" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="resizes" fill="#1976d2" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Gráfico de Pie - Resizes por Producto */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ padding: 2, height: "350px" }}>
                  <Typography variant="h6" gutterBottom>
                    Distribución por Producto
                  </Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={dashboardMetrics.productosChartData}
                        cx="45%"
                        cy="50%"
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                        label={false}
                      >
                        {dashboardMetrics.productosChartData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} instancias`, name]} />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        wrapperStyle={{ fontSize: "11px", paddingLeft: "10px" }}
                        formatter={(value, entry) => `${value} (${entry.payload.value})`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Gráfico de Barras Horizontales - Top 5 Cuentas */}
              <Grid item xs={12}>
                <Paper sx={{ padding: 2, height: "300px" }}>
                  <Typography variant="h6" gutterBottom>
                    Top 5 Cuentas con Más Resizes
                  </Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={dashboardMetrics.cuentasChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#2e7d32" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Tabla Original */}
          <Paper sx={{ padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Detalle de Instancias EC2
            </Typography>
            <TableMaestros
              height={250}
              arrayColumns={columns}
              data={tableData}
              renderDetailPanel={({ original }) => (
                <Box sx={{ padding: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Detalles de Instancia - {original.instance_name || "Sin Nombre"}
                  </Typography>
                  {original.details?.length > 0 ? (
                    <Grid container spacing={2}>
                      {original.details.map((entry, index) => (
                        <React.Fragment key={index}>
                          <Grid item xs={4}>
                            <Typography variant="body2">Días Resize:</Typography>
                            <Typography variant="caption">{mapDays(entry.dias)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2">Hora Base:</Typography>
                            <Typography variant="caption">{entry.hora_base}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2">Hora Resize:</Typography>
                            <Typography variant="caption">{entry.hora_resize}</Typography>
                          </Grid>
                        </React.Fragment>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" sx={{ marginTop: 2 }}>
                      No hay datos para mostrar.
                    </Typography>
                  )}
                </Box>
              )}
              enableGrouping
              enableStickyHeader
              enableColumnResizing
              muiTableContainerProps={{
                sx: { maxHeight: "calc(100vh - 445px)", minHeight: "150px" },
              }}
              muiTableBodyCellProps={{
                sx: {
                  borderRight: "1px solid #e0e0e0",
                  fontSize: 14,
                  padding: "0.5rem",
                },
              }}
              muiTableHeadCellProps={{
                sx: (theme) => ({
                  backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
                  fontSize: "14px",
                  fontWeight: "bold",
                }),
              }}
              renderTopToolbarCustomActions={() => (
                <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
                  <MDButton
                    onClick={handleExportData}
                    startIcon={<FileDownloadIcon />}
                    sx={{ fontSize: "12px", fontWeight: "normal" }}
                  >
                    Exportar Datos
                  </MDButton>
                </Box>
              )}
              initialState={{
                density: "compact",
                pagination: { pageIndex: 0, pageSize: 50 },
              }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

ViewEC2Resizes.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cuenta: PropTypes.string,
      instance_id: PropTypes.string.isRequired,
      instance_name: PropTypes.string,
      producto: PropTypes.string,
      dias: PropTypes.string,
      hora_base: PropTypes.string,
      hora_resize: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
};

export default ViewEC2Resizes;
